import React, { useRef, useState } from "react";
import { View, TextInput, Pressable, Text } from "react-native";

interface OTPInputProps {
  length?: number;
  onComplete: (code: string) => void;
  onChangeText?: (text: string) => void;
  error?: boolean;
}

export function OTPInput({ 
  length = 8, 
  onComplete, 
  onChangeText, 
  error = false 
}: OTPInputProps) {
  const [otp, setOTP] = useState(new Array(length).fill(""));
  const inputs = useRef<TextInput[]>([]);

  const handleChangeText = (text: string, index: number) => {
    // Handle pasting multiple digits
    if (text.length > 1) {
      const pastedDigits = text.slice(0, length).split("");
      const newOTP = new Array(length).fill("");
      
      pastedDigits.forEach((digit, i) => {
        if (i < length && /^\d$/.test(digit)) {
          newOTP[i] = digit;
        }
      });
      
      setOTP(newOTP);
      const otpString = newOTP.join("");
      onChangeText?.(otpString);
      
      // Focus the next empty field or the last field
      const nextEmptyIndex = newOTP.findIndex(digit => digit === "");
      const focusIndex = nextEmptyIndex === -1 ? length - 1 : Math.min(nextEmptyIndex, length - 1);
      inputs.current[focusIndex]?.focus();
      
      if (newOTP.every((digit) => digit !== "")) {
        onComplete(otpString);
      }
      return;
    }

    // Handle single digit input
    if (text && !/^\d$/.test(text)) return;

    const newOTP = [...otp];
    newOTP[index] = text;
    setOTP(newOTP);

    const otpString = newOTP.join("");
    onChangeText?.(otpString);

    if (text && index < length - 1) {
      inputs.current[index + 1]?.focus();
    }

    if (newOTP.every((digit) => digit !== "")) {
      onComplete(otpString);
    }
  };

  const handleKeyPress = (key: string, index: number) => {
    if (key === "Backspace" && !otp[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  return (
    <View className="flex-row w-full h-12">
      {otp.map((digit, index) => {
        const isFirst = index === 0;
        const isLast = index === length - 1;
        
        let borderRadius = "";
        if (isFirst && isLast) {
          borderRadius = "rounded-[17px]";
        } else if (isFirst) {
          borderRadius = "rounded-l-[17px]";
        } else if (isLast) {
          borderRadius = "rounded-r-[17px]";
        }
        
        return (
          <Pressable
            key={index}
            className={`bg-teachLoop-creamLight border ${
              error ? "border-red-500" : "border-gray-200"
            } ${borderRadius} items-center justify-center flex-1 h-full ${
              !isLast ? "border-r-0" : ""
            }`}
            onPress={() => inputs.current[index]?.focus()}
          >
            <TextInput
              ref={(ref) => {
                if (ref) inputs.current[index] = ref;
              }}
              className="text-center font-geologica-medium text-lg text-teachLoop-textPrimary w-full h-full opacity-0 absolute"
              value={digit}
              onChangeText={(text) => handleChangeText(text, index)}
              onKeyPress={({ nativeEvent }) => handleKeyPress(nativeEvent.key, index)}
              keyboardType="number-pad"
              selectTextOnFocus
              autoFocus={index === 0}
            />
            <Text
              className={`font-geologica-medium text-xl ${
                digit ? 'text-teachLoop-textPrimary' : 'text-gray-400'
              }`}
            >
              {digit || '•'}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}